import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
    FormControlLabel,
    FormGroup,
    styled,
    Switch,
    SwitchProps,
    Chip,
    Menu,
    MenuItem,
    Autocomplete,
    Tabs,
    Tab
} from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";
import * as React from "react";
import { Edit, Save, Warning } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { urls } from "@routes";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { format } from "date-fns";

interface PageData {
    status_code: string
    status_text: string


    onboarding_email: boolean
    blacklist_email: boolean
    campaign_pause_email: boolean
    enable_contact_verification: boolean
}


interface AdminEmailTemplates {
    key: string;
    subject: string;
    body_html: string;
    updated_at: string
}

interface PaginationData {
    data: AdminEmailTemplates[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
}

interface CustomizedSwitchProps {
    checked: boolean;
    onChange: (value: boolean) => void;
    disabled?: boolean;
}

// These email toggles only take effect while the SPF/DKIM/DMARC flow itself is enabled.
const EMAIL_AUTH_CHILD_TOGGLES: EmailToggleKey[] = [
    "email_auth_campaign_pause_email",
    "email_auth_warmup_pause_email",
];

// Burnt-domain pause emails only fire when the matching pause is enabled on the Feature Flags
// page, so each one is shown under the flag that governs it.
const BURNT_DOMAIN_EMAIL_PARENTS: Record<string, EmailToggleKey> = {
    burnt_domain_warmup_pause_email: "burnt_domain_pause_warmup",
    burnt_domain_campaign_pause_email: "burnt_domain_pause_running_campaigns",
};

// Keys match the backend's Campaign.bot_filter_* fields.
const BOT_FILTER_PROVIDERS = [
    {field: "bot_filter_google", label: "Google"},
    {field: "bot_filter_microsoft", label: "Microsoft 365"},
    {field: "bot_filter_zoho", label: "Zoho"},
    {field: "bot_filter_other", label: "Other Providers"},
] as const;

type BotFilterField = typeof BOT_FILTER_PROVIDERS[number]["field"];

const DEFAULT_BOT_DETECTION_WINDOWS: Record<BotFilterField, number> = {
    bot_filter_google: 40,
    bot_filter_microsoft: 40,
    bot_filter_zoho: 40,
    bot_filter_other: 40,
};

interface BotDetectionSettings {
    bot_detection_windows?: Partial<Record<BotFilterField, number>>;
    bot_detection_window_default?: number;
}

export type EmailToggleKey =
    | "blacklist_email"
    | "onboarding_email"
    | "campaign_pause_email"
    | "enable_contact_verification"
    | "enable_new_plan_purchase_email_message"
    | "enable_new_integration_request_email_message"
    | "enable_approval_request_email_message"
    | "enable_reset_password_email_message"
    | "enable_new_signup_email_message"
    | "enable_domain_connected_email_message"
    | "enable_plan_renewal_paused_campaigns_email_message"
    | "enable_campaign_completed_email_message"
    | "enable_campaign_resumed_email_message"
    | "enable_campaign_scheduled_start_cancelled_email_message"
    | "enable_high_bounce_rate_email_body"
    | "enable_approval_request_rejected_email_message"
    | "enable_payment_incomplete_day_0_email_message"
    | "enable_payment_incomplete_day_2_email_message"
    | "enable_payment_incomplete_day_4_email_message"
    | "enable_payment_incomplete_day_6_email_message"
    | "enable_account_email_verification_email_body"
    | "enable_approval_request_passed_email_message"
    | "enable_daily_stats_report_email_message"
    | "enable_campaign_creation_reminder_email_message"
    | "enable_domain_connection_reminder_email_message"
    | "enable_domain_warmed_up_email_message"
    | "enable_credits_exhausted_email_message"
    | "enable_campaign_start_email_message"
    | "enable_ns_record_verify_detection"
    | "enable_google_postmaster_spam_detection"
    | "campaign_spam_pause_email"
    | "campaign_ns_pause_email"
    | "enable_email_verifier_alert"
    | "master_inbox_enabled"
    | "campaign_ns_resume_email"
    | "campaign_ns_reschedule_email"
    | "campaign_ns_mismatch_email"
    | "duplicate_campaign_email"
    | "email_auth_auto_pause_enabled"
    | "email_auth_campaign_pause_email"
    | "email_auth_warmup_pause_email"
    | "burnt_domain_pause_warmup"
    | "burnt_domain_pause_running_campaigns"
    | "burnt_domain_warmup_pause_email"
    | "burnt_domain_campaign_pause_email" 
    | "enable_auto_response_failed" ;

export default function AdminEmailSettings() {
    const { enqueueSnackbar } = useSnackbar();
    const [emailToggles, setEmailToggles] = useState<Record<EmailToggleKey, boolean>>({
        blacklist_email: false,
        onboarding_email: false,
        campaign_pause_email: false,
        enable_contact_verification: false,
        enable_new_plan_purchase_email_message: false,
        enable_new_integration_request_email_message: false,
        enable_approval_request_email_message: false,
        enable_reset_password_email_message: false,
        enable_new_signup_email_message: false,
        enable_domain_connected_email_message: false,
        enable_plan_renewal_paused_campaigns_email_message: false,
        enable_campaign_completed_email_message: false,
        enable_campaign_resumed_email_message: false,
        enable_campaign_scheduled_start_cancelled_email_message: false,
        enable_high_bounce_rate_email_body: false,
        enable_approval_request_rejected_email_message: false,
        enable_payment_incomplete_day_0_email_message: false,
        enable_payment_incomplete_day_2_email_message: false,
        enable_payment_incomplete_day_4_email_message: false,
        enable_payment_incomplete_day_6_email_message: false,
        enable_account_email_verification_email_body: false,
        enable_approval_request_passed_email_message: false,
        enable_daily_stats_report_email_message: false,
        enable_campaign_creation_reminder_email_message: false,
        enable_domain_connection_reminder_email_message: false,
        enable_domain_warmed_up_email_message: false,
        enable_credits_exhausted_email_message: false,
        enable_campaign_start_email_message: false,
        enable_ns_record_verify_detection: false,
        enable_google_postmaster_spam_detection: false,
        campaign_spam_pause_email: false,
        enable_email_verifier_alert: false,
        campaign_ns_pause_email: false,
        master_inbox_enabled: false,
        campaign_ns_resume_email: false,
        campaign_ns_reschedule_email: false,
        campaign_ns_mismatch_email: false,
        duplicate_campaign_email: false,
        email_auth_auto_pause_enabled: false,
        email_auth_campaign_pause_email: false,
        email_auth_warmup_pause_email: false,
        burnt_domain_pause_warmup: false,
        burnt_domain_pause_running_campaigns: false,
        burnt_domain_warmup_pause_email: false,
        burnt_domain_campaign_pause_email: false,
        enable_auto_response_failed: false,
    });
    const [tabValue, setTabValue] = useState<number>(0);
    const [botDetectionWindows, setBotDetectionWindows] =
        useState<Record<BotFilterField, number>>(DEFAULT_BOT_DETECTION_WINDOWS);
    const [botDetectionWindowDefault, setBotDetectionWindowDefault] = useState<number>(40);

    // Fetch the page data
    const pageDataQuery = useQuery({
        queryKey: ["adminEmailSetting"],
        queryFn: () => authenticateAndFetchData("/admin/get-email-settings/"),
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
        if (!pageDataQuery.data) return;

        const data = pageDataQuery.data.data as Record<EmailToggleKey, boolean>;

        setEmailToggles(data);

        const botSettings = pageDataQuery.data.data as BotDetectionSettings;
        setBotDetectionWindows({
            ...DEFAULT_BOT_DETECTION_WINDOWS,
            ...(botSettings.bot_detection_windows ?? {}),
        });
        setBotDetectionWindowDefault(botSettings.bot_detection_window_default ?? 40);
    }, [pageDataQuery.data]);


    // Mutation for saving workspace settings.
    const emailSettingsMutation = useMutation({
        mutationKey: ["emailSettings"],
        mutationFn: async (updates: Partial<Record<EmailToggleKey, boolean>>) => {
            return authenticateAndPostData("/admin/email-settings/", updates);
        },
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("Successfully updated email settings!", { variant: "success" });
            pageDataQuery.refetch();
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(error.data.message, { variant: "error" });
        },
    });

    const handleToggle = (key: EmailToggleKey, value: boolean) => {
        const updates: Partial<Record<EmailToggleKey, boolean>> = { [key]: value };
        if (key === "email_auth_auto_pause_enabled" && !value) {
            EMAIL_AUTH_CHILD_TOGGLES.forEach(childKey => {
                updates[childKey] = false;
            });
        }

        setEmailToggles(prev => ({
            ...prev,
            ...updates,
        }));

        emailSettingsMutation.mutate(updates);
    };

    // Bot detection windows are numbers, so they need their own endpoint - /admin/email-settings/
    // coerces every value it accepts to a boolean - and an explicit Save rather than save-on-change.
    const botDetectionMutation = useMutation({
        mutationKey: ["botDetectionSettings"],
        mutationFn: async () => authenticateAndPostData("/admin/bot-detection-settings/", {
            bot_detection_windows: botDetectionWindows,
            bot_detection_window_default: botDetectionWindowDefault,
        }),
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("Successfully updated bot detection windows!", { variant: "success" });
            pageDataQuery.refetch();
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(error.data.message, { variant: "error" });
        },
    });


    function CustomizedSwitch({ checked, onChange, disabled = false }: CustomizedSwitchProps) {
        const IOSSwitch = styled((props: SwitchProps) => (
            <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
        ))(({ theme }) => ({
            width: 42,
            height: 26,
            padding: 0,
            '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: 2,
                transitionDuration: '300ms',
                '&.Mui-checked': {
                    transform: 'translateX(16px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                        backgroundColor: '#0288d1',
                        opacity: 1,
                        border: 0,
                        ...theme.applyStyles('dark', {
                            backgroundColor: '#0288d1',
                        }),
                    },
                    '&.Mui-disabled + .MuiSwitch-track': {
                        opacity: 0.5,
                    },
                },
                '&.Mui-focusVisible .MuiSwitch-thumb': {
                    color: '#0288d1',
                    border: '6px solid #fff',
                },
                '&.Mui-disabled .MuiSwitch-thumb': {
                    color: theme.palette.grey[100],
                    ...theme.applyStyles('dark', {
                        color: theme.palette.grey[600],
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.7,
                    ...theme.applyStyles('dark', {
                        opacity: 0.3,
                    }),
                },
            },
            '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: 22,
                height: 22,
            },
            '& .MuiSwitch-track': {
                borderRadius: 26 / 2,
                backgroundColor: '#E9E9EA',
                opacity: 1,
                transition: theme.transitions.create(['background-color'], {
                    duration: 500,
                }),
                ...theme.applyStyles('dark', {
                    backgroundColor: '#39393D',
                }),
            },
        }));

        return (
            <FormGroup sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: '78px' }}>
                <FormControlLabel
                    control={<IOSSwitch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />}
                    label=""
                />
            </FormGroup>
        );
    }

    const handleTabChange = async (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    if (pageDataQuery.isLoading) {
        return (
            <PageLoading />
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />

    } else {
        return (
            <Stack direction="column" spacing={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4">Email Settings</Typography>
                </Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} sx={{
                        '& .MuiTabs-flexContainer': {
                            flexWrap: 'wrap',
                        },
                    }}>
                        <Tab label="Email toggles" />
                        {/* <Tab label="Feature Flags" /> */}
                        <Tab label="Email Templates" />
                    </Tabs>
                </Box>

                {tabValue === 0 &&
                    <Stack direction="column" spacing={2} sx={{ pb: 4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.onboarding_email}
                                onChange={(value) => handleToggle("onboarding_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Onboarding Email
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.campaign_pause_email}
                                onChange={(value) => handleToggle("campaign_pause_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign Pause Email
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.blacklist_email}
                                onChange={(value) => handleToggle("blacklist_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Blacklist Email
                            </Typography>
                        </Box>

                        {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_contact_verification}
                                onChange={(value) => handleToggle("enable_contact_verification", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Contact List Verification
                            </Typography>
                        </Box> */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_new_plan_purchase_email_message}
                                onChange={(value) => handleToggle("enable_new_plan_purchase_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable New Plan Purchase Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_new_integration_request_email_message}
                                onChange={(value) => handleToggle("enable_new_integration_request_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable New Integration Request Email
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_approval_request_email_message}
                                onChange={(value) => handleToggle("enable_approval_request_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Approval Request Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_reset_password_email_message}
                                onChange={(value) => handleToggle("enable_reset_password_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Reset Password Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_new_signup_email_message}
                                onChange={(value) => handleToggle("enable_new_signup_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable New Signup Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_domain_connected_email_message}
                                onChange={(value) => handleToggle("enable_domain_connected_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Domain Connected Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_plan_renewal_paused_campaigns_email_message}
                                onChange={(value) => handleToggle("enable_plan_renewal_paused_campaigns_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Plan Renewal Paused Campaigns Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_campaign_completed_email_message}
                                onChange={(value) => handleToggle("enable_campaign_completed_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign Completed Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_campaign_resumed_email_message}
                                onChange={(value) => handleToggle("enable_campaign_resumed_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign Resumed Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_campaign_scheduled_start_cancelled_email_message}
                                onChange={(value) => handleToggle("enable_campaign_scheduled_start_cancelled_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign Scheduled Start Cancelled Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_high_bounce_rate_email_body}
                                onChange={(value) => handleToggle("enable_high_bounce_rate_email_body", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable High Bounce Rate Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_approval_request_rejected_email_message}
                                onChange={(value) => handleToggle("enable_approval_request_rejected_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Approval Request Rejected Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_payment_incomplete_day_4_email_message}
                                onChange={(value) => handleToggle("enable_payment_incomplete_day_4_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Payment Incomplete Day 4 Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_payment_incomplete_day_2_email_message}
                                onChange={(value) => handleToggle("enable_payment_incomplete_day_2_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Payment Incomplete Day 2 Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_payment_incomplete_day_6_email_message}
                                onChange={(value) => handleToggle("enable_payment_incomplete_day_6_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Payment Incomplete Day 6 Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_account_email_verification_email_body}
                                onChange={(value) => handleToggle("enable_account_email_verification_email_body", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Account Email Verification Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_approval_request_passed_email_message}
                                onChange={(value) => handleToggle("enable_approval_request_passed_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Approval Request Passed Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_daily_stats_report_email_message}
                                onChange={(value) => handleToggle("enable_daily_stats_report_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Daily Stats Report Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_campaign_creation_reminder_email_message}
                                onChange={(value) => handleToggle("enable_campaign_creation_reminder_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign Creation Reminder Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_domain_connection_reminder_email_message}
                                onChange={(value) => handleToggle("enable_domain_connection_reminder_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Domain Connection Reminder Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_domain_warmed_up_email_message}
                                onChange={(value) => handleToggle("enable_domain_warmed_up_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Domain Warmed Up Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_credits_exhausted_email_message}
                                onChange={(value) => handleToggle("enable_credits_exhausted_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Credits Exhausted Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_campaign_start_email_message}
                                onChange={(value) => handleToggle("enable_campaign_start_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign Start Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_payment_incomplete_day_0_email_message}
                                onChange={(value) => handleToggle("enable_payment_incomplete_day_0_email_message", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Payment Incomplete Day 0 Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_ns_record_verify_detection}
                                onChange={(value) => handleToggle("enable_ns_record_verify_detection", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Ns Record Verify Detection
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_google_postmaster_spam_detection}
                                onChange={(value) => handleToggle("enable_google_postmaster_spam_detection", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Google Postmaster Spam Detection
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.campaign_spam_pause_email}
                                onChange={(value) => handleToggle("campaign_spam_pause_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign spam pause email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.campaign_ns_pause_email}
                                onChange={(value) => handleToggle("campaign_ns_pause_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Campaign ns pause email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_email_verifier_alert}
                                onChange={(value) => handleToggle("enable_email_verifier_alert", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Email Verifier alert
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.campaign_ns_resume_email}
                                onChange={(value) => handleToggle("campaign_ns_resume_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable NS Resume Campaign Alert
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.campaign_ns_reschedule_email}
                                onChange={(value) => handleToggle("campaign_ns_reschedule_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable NS Reschedule Campaign Alert
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.campaign_ns_mismatch_email}
                                onChange={(value) => handleToggle("campaign_ns_mismatch_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable NS Mismatch Email Alert
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.duplicate_campaign_email}
                                onChange={(value) => handleToggle("duplicate_campaign_email", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Duplicate Campaign with pending leads Email Alert
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.email_auth_auto_pause_enabled}
                                onChange={(value) => handleToggle("email_auth_auto_pause_enabled", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable SPF/DKIM/DMARC Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 6 }}>
                            <CustomizedSwitch
                                checked={emailToggles.email_auth_campaign_pause_email}
                                disabled={!emailToggles.email_auth_auto_pause_enabled}
                                onChange={(value) => handleToggle("email_auth_campaign_pause_email", value)}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold" }}
                                color={emailToggles.email_auth_auto_pause_enabled ? "text.primary" : "text.disabled"}
                            >
                                Enable SPF/DKIM/DMARC Campaign Paused / Records Fixed Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 6 }}>
                            <CustomizedSwitch
                                checked={emailToggles.email_auth_warmup_pause_email}
                                disabled={!emailToggles.email_auth_auto_pause_enabled}
                                onChange={(value) => handleToggle("email_auth_warmup_pause_email", value)}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold" }}
                                color={emailToggles.email_auth_auto_pause_enabled ? "text.primary" : "text.disabled"}
                            >
                                Enable SPF/DKIM/DMARC Warmup Paused / Resumed Email
                            </Typography>
                        </Box>

                        <Divider sx={{ pt: 2 }} />

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.burnt_domain_warmup_pause_email}
                                disabled={!emailToggles[BURNT_DOMAIN_EMAIL_PARENTS.burnt_domain_warmup_pause_email]}
                                onChange={(value) => handleToggle("burnt_domain_warmup_pause_email", value)}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold" }}
                                color={emailToggles.burnt_domain_pause_warmup ? "text.primary" : "text.disabled"}
                            >
                                Enable Burnt Domain Warmup Paused Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.burnt_domain_campaign_pause_email}
                                disabled={!emailToggles[BURNT_DOMAIN_EMAIL_PARENTS.burnt_domain_campaign_pause_email]}
                                onChange={(value) => handleToggle("burnt_domain_campaign_pause_email", value)}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold" }}
                                color={emailToggles.burnt_domain_pause_running_campaigns ? "text.primary" : "text.disabled"}
                            >
                                Enable Burnt Domain Campaigns Paused Email
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.enable_auto_response_failed}
                                onChange={(value) => handleToggle("enable_auto_response_failed", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Auto Response Failed Email Alert
                            </Typography>
                        </Box>

                        <Divider sx={{ pt: 2 }} />

                        <Box>
                            <Typography variant="h6">Bot Detection Windows</Typography>
                            <Typography variant="body2" color="text.secondary">
                                An open or click landing within this many seconds of sending is counted as bot
                                activity rather than engagement. Applies to every campaign; campaigns only choose
                                which providers bot filtering runs for.
                            </Typography>

                            <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 2 }}>
                                {BOT_FILTER_PROVIDERS.map(({ field, label }) => (
                                    <TextField
                                        key={field}
                                        label={label}
                                        type="number"
                                        size="small"
                                        sx={{ width: 200 }}
                                        value={botDetectionWindows[field]}
                                        onChange={(event) => setBotDetectionWindows(previous => ({
                                            ...previous,
                                            [field]: Math.min(90, Math.max(0, parseInt(event.target.value, 10) || 0)),
                                        }))}
                                        inputProps={{ min: 0, max: 90 }}
                                        helperText="Seconds after send (0-90)"
                                    />
                                ))}
                                <TextField
                                    label="Unknown Provider"
                                    type="number"
                                    size="small"
                                    sx={{ width: 200 }}
                                    value={botDetectionWindowDefault}
                                    onChange={(event) => setBotDetectionWindowDefault(
                                        Math.min(90, Math.max(0, parseInt(event.target.value, 10) || 0))
                                    )}
                                    inputProps={{ min: 0, max: 90 }}
                                    helperText="Used when the ESP is unknown"
                                />
                            </Stack>

                            <Button
                                variant="contained"
                                sx={{ mt: 2 }}
                                disabled={botDetectionMutation.isPending}
                                onClick={() => botDetectionMutation.mutate()}
                            >
                                {botDetectionMutation.isPending ? "Saving..." : "Save"}
                            </Button>
                        </Box>
                    </Stack>
                }
                {/* {
                    tabValue === 1 &&
                    <Stack direction="column" spacing={2} sx={{ pb: 4 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Control which features are visible and accessible to all users.
                        </Typography>
                        <Divider />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CustomizedSwitch
                                checked={emailToggles.master_inbox_enabled}
                                onChange={(value) => handleToggle("master_inbox_enabled", value)}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Master Inbox
                            </Typography>
                        </Box>
                    </Stack>
                } */}
                {
                    tabValue === 1 &&
                    <EmailTemplates />
                }
            </Stack >
        )
    }
}

function EmailTemplates(props: {}) {

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState("");
    const [pageData, setPageData] = useState<PaginationData>();    
    const [placeholders, setPlaceholders] = useState({})
    const pageDataQuery = useQuery({
        queryKey: ["adminEmailTemplates", page, pageSize, search],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/get-email-template/?page=${page}&page_size=${pageSize}&search=${search}`
            ),
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
    if (pageDataQuery.data?.data) {
        const response = pageDataQuery.data.data;

            setPageData({
                data: response.data || [],
                total_count: response.total_count,
                total_pages: response.total_pages,
                current_page: response.current_page,
                page_size: response.page_size,
            });

            setPlaceholders(response.sample_placeholders || {});
        }
    }, [pageDataQuery.data]);

    function timestampFormatter(params: ValueFormatterParams) {
        if (params.value !== null) {
            return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
        } else {
            return "---"
        }
    }

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const columnDefs: ColDef[] = [
        { field: "subject", headerName: "Subject" },
        { field: 'updated_at', headerName: "Updated On", valueFormatter: timestampFormatter },
        {
            field: "action", headerName: "Action", resizable: false,
            cellRenderer: (params: any) => {
                if (params.data?.key) {
                    return (
                        <EditEmailTemplate
                            templateKey={params.data.key}
                            subject={params.data.subject}
                            body={params.data.body_html}
                            placeholder={placeholders}
                            refetch={() => {
                                pageDataQuery.refetch().then();
                            }}
                        />
                    );
                }
                return null;
            },
        },
    ]

    if (!pageData && pageDataQuery.isLoading) {
        return (
            <PageLoading />
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />

    } else if (pageData) {
        return (
            <>
                <Box sx={{ height: 600, width: "100%", overflow: "hidden" }}>
                    <PaginatedServerSideDataTable
                        columns={columnDefs}
                        paginationData={pageData}
                        loading={pageDataQuery.isLoading || pageDataQuery.isFetching}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search by subject or key..."
                    />
                </Box>
            </>
        )
    } else {
        // Ideally it should not reach here.
        return <></>
    }
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": { padding: theme.spacing(2) },
    "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));


function EditEmailTemplate(props: {
    templateKey: string;
    subject: string;
    body: string;
    placeholder: {};
    refetch: () => void;
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = useState(false);
    const [editSubject, setEditSubject] = useState(props.subject);
    const [editBody, setEditBody] = useState(props.body);
    const [viewMode, setViewMode] = useState<"edit" | "html">("edit");

    // --- Track unsaved changes ---
    const hasUnsavedChanges =
        editSubject !== props.subject || editBody !== props.body;

    // --- Save template mutation ---
    const saveEmailTemplateMutation = useMutation({
        mutationKey: ["saveEmailTemplateMutation", props.templateKey],
        mutationFn: () =>
            authenticateAndPostData("/admin/save-email-template/", {
                key: props.templateKey,
                subject: editSubject,
                body_html: editBody,
            }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("Email template updated successfully!", {
                variant: "success",
            });
            props.refetch();
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data?.message || "Failed to update template", {
                variant: "error",
            });
        },
    });

    // --- Send test email mutation ---
    const sendTestEmailTemplateMutation = useMutation({
        mutationKey: ["sendTestEmailTemplateMutation", props.templateKey],
        mutationFn: () =>
            authenticateAndPostData("/admin/send-test-email-template/", {
                key: props.templateKey,
            }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("Test email sent successfully!", { variant: "success" });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data?.message || "Failed to send test email", {
                variant: "error",
            });
        },
    });

    // --- Dialog open/close ---
    const handleClickOpen = () => {
        setViewMode("edit");
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    // --- Replace placeholders in preview ---
    // --- Replace placeholders in preview ---
    const decodeHTML = (str: string) => {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = str;
        return textarea.value;
    };

    const renderWithPlaceholders = (html: string) => {
        if (!html) return "";

        // Decode HTML entities properly
        const decoded = decodeHTML(html);

        // Get sample data from props or fallback empty object
        const sampleData = props.placeholder || {};

        // Replace both normal and HTML-escaped curly braces
        return decoded.replace(/\{+(\s*[\w_]+\s*)\}+|&#123;(\s*[\w_]+\s*)&#125;/g, (match, key1, key2) => {
            const key = (key1 || key2 || "").trim();
            return sampleData[key] ?? "";
        });
    };


    console.log("renderWithPlaceholders", renderWithPlaceholders)
    // --- Manual Save Handler ---
    const handleSave = () => {
        saveEmailTemplateMutation.mutate();
    };

    // --- Send Test Handler ---
    const handleSendTest = () => {
        sendTestEmailTemplateMutation.mutate();
    };

    return (
        <>
            <Button size="small" onClick={handleClickOpen}>
                View Email
            </Button>

            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="edit-email-template-dialog"
                open={open}
                fullWidth
                maxWidth="md"
            >
                <DialogContent dividers>
                    {viewMode === "edit" && (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                fullWidth
                                label="Subject"
                                value={editSubject}
                                onChange={(e) => setEditSubject(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                label="Body (HTML Supported)"
                                multiline
                                minRows={10}
                                maxRows={20}
                                value={editBody}
                                onChange={(e) => setEditBody(e.target.value)}
                                sx={{
                                    "& textarea": {
                                        overflowY: "auto",
                                        maxHeight: "400px",
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {viewMode === "html" && (
                        <div
                            style={{
                                backgroundColor: "#ffffff",
                                color: "#000000",
                                padding: "10px",
                                borderRadius: "6px",
                                minHeight: "300px",
                                overflowY: "auto",
                                maxHeight: "500px",
                            }}
                            dangerouslySetInnerHTML={{
                                __html: renderWithPlaceholders(editBody),
                            }}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    {/* Edit / Save Button */}
                    <Button
                        variant="contained"
                        color={hasUnsavedChanges ? "success" : "primary"}
                        onClick={hasUnsavedChanges ? handleSave : () => setViewMode("edit")}
                        disabled={saveEmailTemplateMutation.isPending}
                    >
                        {saveEmailTemplateMutation.isPending
                            ? "Saving..."
                            : hasUnsavedChanges
                                ? "Save"
                                : "Edit"}
                    </Button>

                    <Button
                        variant={viewMode === "html" ? "contained" : "outlined"}
                        onClick={() => setViewMode("html")}
                    >
                        View HTML
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendTest}
                        disabled={sendTestEmailTemplateMutation.isPending}
                    >
                        {sendTestEmailTemplateMutation.isPending ? "Sending..." : "Send Test"}
                    </Button>

                    <Button onClick={handleClose} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </>
    );
}
